import React, { useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppStore } from './stores/appStore';
import { useAccessibility } from './services/accessibility';
// Import feedbackService to trigger preloading on app start
import './services/feedback';

// Create theme based on mode
const createAppTheme = (darkMode: boolean) => createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    primary: {
      main: '#60a5fa', // Blue for primary actions
      light: '#93c5fd',
      dark: '#3b82f6',
    },
    secondary: {
      main: '#34d399', // Green for secondary actions
      light: '#6ee7b7',
      dark: '#10b981',
    },
    error: {
      main: '#ef4444',
    },
    warning: {
      main: '#f59e0b',
    },
    info: {
      main: '#06b6d4',
    },
    success: {
      main: '#22c55e',
    },
    background: {
      default: darkMode ? '#0f172a' : '#f0f4ff',
      paper: darkMode ? '#1e293b' : '#ffffff',
    },
    text: {
      primary: darkMode ? '#f8fafc' : '#0f172a',
      secondary: darkMode ? 'rgba(248, 250, 252, 0.7)' : 'rgba(15, 23, 42, 0.7)',
    },
  },
  typography: {
    fontFamily: '"Google Sans Flex", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    // M3 Expressive: Emphasized headlines with variable font axes
    h1: {
      fontWeight: 800,
      fontVariationSettings: '"wdth" 100, "GRAD" 50, "ROND" 20',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontVariationSettings: '"wdth" 100, "GRAD" 25, "ROND" 15',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 650,
      fontVariationSettings: '"wdth" 95, "GRAD" 15, "ROND" 10',
    },
    h4: {
      fontWeight: 600,
      fontVariationSettings: '"wdth" 95, "GRAD" 10, "ROND" 10',
    },
    h5: {
      fontWeight: 550,
      fontVariationSettings: '"wdth" 90, "GRAD" 0',
    },
    h6: {
      fontWeight: 500,
      fontVariationSettings: '"wdth" 90, "GRAD" 0',
    },
    // M3 Expressive: Body text with subtle roundedness for friendliness
    body1: {
      fontWeight: 400,
      fontVariationSettings: '"wdth" 100, "ROND" 20',
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      fontVariationSettings: '"wdth" 95, "ROND" 15',
      lineHeight: 1.5,
    },
    // M3 Expressive: Button text - slightly condensed, higher grade for clarity
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontVariationSettings: '"wdth" 95, "GRAD" 50, "ROND" 25',
      letterSpacing: '0.02em',
    },
    // M3 Expressive: Caption - smaller optical size adjustments
    caption: {
      fontWeight: 400,
      fontVariationSettings: '"wdth" 90, "opsz" 12, "ROND" 10',
    },
    // M3 Expressive: Overline - wider, higher grade for emphasis
    overline: {
      fontWeight: 500,
      fontVariationSettings: '"wdth" 110, "GRAD" 100',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12, // Rounded corners throughout
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.15)',
    '0px 8px 16px rgba(0,0,0,0.2)',
    '0px 12px 24px rgba(0,0,0,0.25)',
    '0px 16px 32px rgba(0,0,0,0.3)',
    '0px 20px 40px rgba(0,0,0,0.35)',
    '0px 24px 48px rgba(0,0,0,0.4)',
    '0px 28px 56px rgba(0,0,0,0.45)',
    '0px 32px 64px rgba(0,0,0,0.5)',
    '0px 36px 72px rgba(0,0,0,0.55)',
    '0px 40px 80px rgba(0,0,0,0.6)',
    '0px 44px 88px rgba(0,0,0,0.65)',
    '0px 48px 96px rgba(0,0,0,0.7)',
    '0px 52px 104px rgba(0,0,0,0.75)',
    '0px 56px 112px rgba(0,0,0,0.8)',
    '0px 60px 120px rgba(0,0,0,0.85)',
    '0px 64px 128px rgba(0,0,0,0.9)',
    '0px 68px 136px rgba(0,0,0,0.95)',
    '0px 72px 144px rgba(0,0,0,1)',
    '0px 76px 152px rgba(0,0,0,1)',
    '0px 80px 160px rgba(0,0,0,1)',
    '0px 84px 168px rgba(0,0,0,1)',
    '0px 88px 176px rgba(0,0,0,1)',
    '0px 92px 184px rgba(0,0,0,1)',
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Disable default Material gradient
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          zIndex: 10001, // Ensure notifications appear above FAB taskbars and other overlays
        },
      },
    },
  },
});

// Theme wrapper component that responds to store changes and accessibility
function ThemedApp() {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const toggleHighContrast = useAppStore((state) => state.toggleHighContrast);
  const accessibilityPrefs = useAccessibility();
  
  const theme = useMemo(() => createAppTheme(darkMode), [darkMode]);
  
  // Auto-enable high contrast if system prefers it
  useEffect(() => {
    if (accessibilityPrefs.prefersHighContrast && !highContrast) {
      // System prefers high contrast but app doesn't have it enabled
      // Auto-enable it for better accessibility
      toggleHighContrast();
      console.log('[M3 Accessibility] Auto-enabled high contrast mode based on system preference');
    }
  }, [accessibilityPrefs.prefersHighContrast]);
  
  // Apply high contrast class to document
  useEffect(() => {
    const root = document.documentElement;
    if (highContrast || accessibilityPrefs.prefersHighContrast) {
      root.classList.add('m3-high-contrast');
    } else {
      root.classList.remove('m3-high-contrast');
    }
  }, [highContrast, accessibilityPrefs.prefersHighContrast]);
  
  // Initialize feedback service on first interaction
  useEffect(() => {
    // feedbackService is auto-initialized on first user interaction
    // This log confirms it's loaded
    console.log('[M3 Feedback] Service loaded, will initialize on first interaction');
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  );
}

// Wait for Telegram WebApp to be ready
const init = () => {
  const root = document.getElementById('root');
  if (!root) {
    console.error('Root element not found');
    return;
  }

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ThemedApp />
    </React.StrictMode>
  );
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
